import React, { useEffect, useState, useRef, useMemo } from 'react';

const API_URL = '/api';

// ─── SVG Icons ──────────────────────────────────────────────────────────────
const CloseIcon = ({ size = 24, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const MaximizeIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
  </svg>
);

const MinimizeIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
  </svg>
);

// ─── Searchable Dropdown Component (from your reference) ─────────────────────
const SearchableDropdown = ({
  options,
  value,
  onChange,
  placeholder = "Select",
  error = false,
  width = "w-full",
  getOptionLabel = (opt) => opt.label || opt,
  getOptionValue = (opt) => opt.value || opt,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const term = searchTerm.toLowerCase();
    return options.filter(opt => getOptionLabel(opt).toLowerCase().includes(term));
  }, [options, searchTerm, getOptionLabel]);

  const selectedOption = options.find(opt => getOptionValue(opt) === value);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className={`w-full border rounded-lg px-3 py-2 cursor-pointer flex justify-between items-center 
          ${error ? 'border-red-400' : 'border-gray-300'} 
          ${isOpen ? 'ring-2 ring-[#2b6777]/30' : ''} 
          ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span className="truncate">
          {selectedOption ? getOptionLabel(selectedOption) : placeholder}
        </span>
        <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          <div className="p-2 border-b">
            <input
              type="text"
              placeholder="Search..."
              className="w-full px-3 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#2b6777]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => (
                <div
                  key={getOptionValue(opt)}
                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                  onClick={() => {
                    onChange(getOptionValue(opt));
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                >
                  {getOptionLabel(opt)}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-500">No options found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main Form Modal ────────────────────────────────────────────────────────
const ApiMappingFormModal = ({
  open,
  onClose,
  initialData,
  isEdit,
  onSuccess,
}) => {
  const [fields, setFields] = useState({
    Id: null,
    ApiName: '',
    BaseUrl: '',
    HttpMethod: 'GET',
    TokenUrl: '',
    ClientId: '',
    ClientSecret: '',
    Scope: '',
    GrantType: '',
    UserId: '',
    Password: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);

  const httpMethods = [
    { value: 'GET', label: 'GET' },
    { value: 'POST', label: 'POST' },
    { value: 'PUT', label: 'PUT' },
    { value: 'DELETE', label: 'DELETE' },
    { value: 'PATCH', label: 'PATCH' },
  ];

  useEffect(() => {
    if (open) {
      if (isEdit && initialData) {
        setFields({
          Id: initialData.Id || null,
          ApiName: initialData.ApiName || '',
          BaseUrl: initialData.BaseUrl || '',
          HttpMethod: initialData.HttpMethod || 'GET',
          TokenUrl: initialData.TokenUrl || '',
          ClientId: initialData.ClientId || '',
          ClientSecret: initialData.ClientSecret || '',
          Scope: initialData.Scope || '',
          GrantType: initialData.GrantType || '',
          UserId: initialData.UserId || '',
          Password: initialData.Password || '',
        });
      } else {
        setFields({
          Id: null,
          ApiName: '',
          BaseUrl: '',
          HttpMethod: 'GET',
          TokenUrl: '',
          ClientId: '',
          ClientSecret: '',
          Scope: '',
          GrantType: '',
          UserId: '',
          Password: '',
        });
      }
      setErrors({});
    }
  }, [open, isEdit, initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFields((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleMethodSelect = (value) => {
    setFields((prev) => ({ ...prev, HttpMethod: value }));
    if (errors.HttpMethod) {
      setErrors((prev) => ({ ...prev, HttpMethod: '' }));
    }
  };

  const validate = () => {
    const errs = {};
    if (!fields.ApiName.trim()) errs.ApiName = 'API Name is required';
    if (!fields.BaseUrl.trim()) errs.BaseUrl = 'Base URL is required';
    if (!fields.HttpMethod) errs.HttpMethod = 'HTTP Method is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      const method = isEdit ? 'PUT' : 'POST';
      const url = isEdit
        ? `${API_URL}/mapping/api-mapping/${fields.Id}`
        : `${API_URL}/mapping/api-mapping`;

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' , 'Authorization':`Bearer ${localStorage.getItem('authToken')}`},
        body: JSON.stringify(fields),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || 'Failed to save API mapping');
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      alert(err.message || 'An error occurred while saving');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className={`fixed inset-0 z-50 flex transition-all duration-300 ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Modal content – side drawer from right */}
      <div
        className={`relative ml-auto bg-white w-full max-w-md h-full shadow-2xl transform transition-transform duration-300 ease-in-out overflow-y-auto
          ${open ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white z-10 border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-[#2b6777]">
            {isEdit ? 'Edit API Mapping' : 'Create API Mapping'}
          </h2>
          <div className="flex items-center gap-2">
            {/* <button
              onClick={() => setIsMaximized(!isMaximized)}
              className="p-1 text-gray-500 hover:text-[#2b6777]"
              title={isMaximized ? "Minimize" : "Maximize"}
            >
              {isMaximized ? <MinimizeIcon /> : <MaximizeIcon />}
            </button> */}
            <button onClick={onClose} className="p-1 text-gray-500 hover:text-[#2b6777]">
              <CloseIcon />
            </button>
          </div>
        </div>

        {/* Form content */}
        <div className="p-6 space-y-6">
          {/* API Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              API Name <span className="text-red-500">*</span>
            </label>
            <input
              name="ApiName"
              value={fields.ApiName}
              onChange={handleChange}
              disabled={isEdit}
              className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#2b6777]/30
                ${errors.ApiName ? 'border-red-400' : 'border-gray-300'}`}
              placeholder="e.g. GetUserProfile"
            />
            {errors.ApiName && <p className="mt-1 text-xs text-red-500">{errors.ApiName}</p>}
          </div>

          {/* Base URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Base URL <span className="text-red-500">*</span>
            </label>
            <input
              name="BaseUrl"
              value={fields.BaseUrl}
              onChange={handleChange}
              className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#2b6777]/30
                ${errors.BaseUrl ? 'border-red-400' : 'border-gray-300'}`}
              placeholder="https://api.example.com/v1"
            />
            {errors.BaseUrl && <p className="mt-1 text-xs text-red-500">{errors.BaseUrl}</p>}
          </div>

          {/* HTTP Method – using SearchableDropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              HTTP Method <span className="text-red-500">*</span>
            </label>
            <SearchableDropdown
              options={httpMethods}
              value={fields.HttpMethod}
              onChange={handleMethodSelect}
              placeholder="Select HTTP Method"
              error={!!errors.HttpMethod}
              getOptionLabel={(opt) => opt.label}
              getOptionValue={(opt) => opt.value}
            />
            {errors.HttpMethod && <p className="mt-1 text-xs text-red-500">{errors.HttpMethod}</p>}
          </div>

          {/* Optional fields */}
          {[
            { name: 'TokenUrl', label: 'Token URL', type: 'url' },
            { name: 'ClientId', label: 'Client ID' },
            { name: 'ClientSecret', label: 'Client Secret', type: 'password' },
            { name: 'Scope', label: 'Scope' },
            { name: 'GrantType', label: 'Grant Type' },
            { name: 'UserId', label: 'User ID' },
            { name: 'Password', label: 'Password', type: 'password' },
          ].map((field) => (
            <div key={field.name}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {field.label}
              </label>
              <input
                name={field.name}
                type={field.type || 'text'}
                value={fields[field.name]}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#2b6777]/30"
                placeholder={`Enter ${field.label.toLowerCase()}`}
              />
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-lg text-gray-600 hover:bg-gray-100 transition"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className={`px-6 py-2 rounded-lg text-white font-medium transition
              ${loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#2b6777] hover:bg-[#1e4a56]'}`}
          >
            {loading ? 'Saving...' : isEdit ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApiMappingFormModal;